import { useCallback, useRef, useSyncExternalStore } from 'react'
import type { WebsocketProvider } from 'y-websocket'
import type { Presence } from '@praesto/shared'

export interface RemotePresence extends Presence {
  clientId: number
}

function readRemotes(provider: WebsocketProvider): RemotePresence[] {
  const local = provider.awareness.clientID
  const result: RemotePresence[] = []
  provider.awareness.getStates().forEach((state, clientId) => {
    if (clientId === local) return
    const presence = state as Partial<Presence>
    if (!presence.user) return
    result.push({
      clientId,
      user: presence.user,
      cursor: presence.cursor ?? null,
      viewport: presence.viewport ?? null,
      focus: presence.focus ?? null,
      presenting: presence.presenting ?? false,
      following: presence.following ?? false,
      pointer: presence.pointer ?? null,
    })
  })
  return result
}

/** Presence aller *anderen* Teilnehmer. Der eigene Zustand ist bewusst nicht dabei. */
export function usePresence(provider: WebsocketProvider | null): RemotePresence[] {
  const cache = useRef<RemotePresence[]>([])
  const stale = useRef(true)

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!provider) return () => {}
      const handler = (): void => {
        stale.current = true
        onChange()
      }
      provider.awareness.on('change', handler)
      return () => provider.awareness.off('change', handler)
    },
    [provider],
  )

  const getSnapshot = useCallback(() => {
    if (!provider) return cache.current
    if (stale.current) {
      cache.current = readRemotes(provider)
      stale.current = false
    }
    return cache.current
  }, [provider])

  return useSyncExternalStore(subscribe, getSnapshot)
}
